import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  Grid,
  Card,
  CardContent,
  Button,
  Avatar,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  OutlinedInput,
  Switch,
  FormControlLabel,
  Slider,
  Divider,
  CircularProgress,
  Alert,
  InputAdornment,
  Collapse,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  CardMedia,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from "@mui/material";
import {
  styled,
  createTheme,
  ThemeProvider as MuiThemeProvider,
} from "@mui/material/styles";
import Wrapper from "../../../components/TenantWrapper";
import { useDispatch, useSelector } from "react-redux";
import { toast, ToastContainer } from "react-toastify";
import title from "../../../components/title";
import { useTheme as useAppTheme } from "../../../context/ThemeContext";

// Icons
import LockIcon from "@mui/icons-material/Lock";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import PersonIcon from "@mui/icons-material/Person";
import HomeIcon from "@mui/icons-material/Home";
import WorkIcon from "@mui/icons-material/Work";
import PetsIcon from "@mui/icons-material/Pets";
import ContactPhoneIcon from "@mui/icons-material/ContactPhone";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import TwoWheelerIcon from "@mui/icons-material/TwoWheeler";
import BadgeIcon from "@mui/icons-material/Badge";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import BusinessIcon from "@mui/icons-material/Business";
import SchoolIcon from "@mui/icons-material/School";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import DrawIcon from "@mui/icons-material/Draw";
import InstallAppCard from "../../../components/InstallAppCard";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import DescriptionIcon from "@mui/icons-material/Description";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import Paper from "@mui/material/Paper";

import { GetTenantProfile } from "../../../redux/reducer/GetTenantProfile";
import {
  UpdateTenantProfile,
  PreviewTenantProfileUpdate,
} from "../../../redux/reducer/UpdateTenantProfile";
import BookingCancellationDialog from "./BookingCancellationDialog";
import { ValidateUser } from "../../../redux/reducer/ValidateUser";

// Styled Components
const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
  marginBottom: theme.spacing(3),
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  marginBottom: theme.spacing(2),
  paddingBottom: theme.spacing(1),
  borderBottom: "1px solid #eee",
}));

const LockedField = styled(Box)(({ theme }) => ({
  position: "relative",
  "& .MuiTextField-root": {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#f5f5f5",
    },
  },
}));

const LockBadge = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: -8,
  right: -8,
  backgroundColor: "#ff9800",
  borderRadius: "50%",
  padding: 4,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1,
}));

const PhotoUploadBox = styled(Box)(({ theme }) => ({
  width: 120,
  height: 120,
  borderRadius: "50%",
  border: "3px dashed #ccc",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  overflow: "hidden",
  transition: "all 0.3s ease",
  "&:hover": {
    borderColor: theme.palette.primary.main,
    backgroundColor: `${theme.palette.primary.main}0a`,
  },
}));

const DocumentCard = styled(Card)(({ theme }) => ({
  cursor: "pointer",
  transition: "all 0.3s ease",
  height: "100%",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
  },
}));

const ImageContainer = styled(Box)({
  position: "relative",
  width: "100%",
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  overflow: "hidden",
});

const pakistaniLanguages = [
  "Urdu",
  "English",
  "Punjabi",
  "Saraiki",
  "Sindhi",
  "Pashto",
  "Balochi",
  "Brahui",
  "Hindko",
  "Kashmiri",
  "Pahari",
  "Pahari-Pothwari",
  "Gojri (Gujari)",
  "Shina",
  "Kohistani Shina",
  "Indus Kohistani",
  "Bateri",
  "Chilisso",
  "Gowro",
  "Torwali",
  "Gawri (Kalam Kohistani)",
  "Kalkoti",
  "Ushojo",
  "Khowar",
  "Kalasha (Kalasha-mun)",
  "Dameli",
  "Gawar-Bati",
  "Palula",
  "Yidgha",
  "Ormuri",
  "Burushaski",
  "Balti",
  "Wakhi",
  "Domaaki",
  "Kundal Shahi",
  "Jadgali",
  "Kachhi",
  "Dhatki",
  "Bagri",
  "Marwari",
  "Thari",
  "Parkari Koli",
  "Koli",
  "Kutchi (Kachchhi)",
  "Memoni",
  "Gujarati",
  "Dawoodi Bohra Gujarati",
  "Konkani",
  "Bengali",
  "Rohingya",
  "Burmese",
  "Tamil",
  "Telugu",
  "Malayalam",
  "Sinhala",
  "Persian (Farsi)",
  "Dari",
  "Hazaragi",
  "Arabic",
  "Turkmen",
  "Uzbek",
  "Kyrgyz",
  "Kazakh",
  "Uyghur",
  "Mandarin Chinese",
  "Cantonese",
  "Russian",
  "Armenian",
  "Nepali",
  "Dogri",
  "Chitrali (often used locally for Khowar)",
  "Siraiki Hindki (local variety)",
  "Bhaya",
  "Aer",
  "Loarki",
  "Sansi",
  "Od (Oadki)",
];

const incomeRanges = [
  { value: "below_25k", label: "Below 25,000" },
  { value: "25k_50k", label: "25,000 - 50,000" },
  { value: "50k_75k", label: "50,000 - 75,000" },
  { value: "75k_100k", label: "75,000 - 1,00,000" },
  { value: "100k_150k", label: "1,00,000 - 1,50,000" },
  { value: "above_150k", label: "Above 1,50,000" },
];

const moveInOptions = [
  { value: "immediate", label: "Immediately" },
  { value: "15_days", label: "15 Days" },
  { value: "1_month", label: "1 Month" },
  { value: "2_months", label: "2 Months" },
  { value: "flexible", label: "Flexible" },
];

const petTypes = [
  { value: "cat", label: "Cat" },
  { value: "dog", label: "Dog" },
  { value: "bird", label: "Bird" },
  { value: "fish", label: "Fish" },
  { value: "small mammal", label: "Small Mammal" },
  { value: "reptile", label: "Reptile" },
  { value: "other", label: "Other" },
];

const vehicleTypes = [
  { value: "car", label: "Car" },
  { value: "bike", label: "Bike" },
];

// Image Modal Component
function ImageModal({ open, onClose, imageUrl, title }) {
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);

  useEffect(() => {
    if (!open) {
      setZoom(1);
      setRotation(0);
    }
  }, [open]);

  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(prev - 0.25, 0.5));
  };

  const handleRotate = () => {
    setRotation((prev) => (prev + 90) % 360);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: "#000",
          color: "#fff",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid rgba(255,255,255,0.1)",
        }}
      >
        <Typography variant="h6" color="white">
          {title}
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <IconButton onClick={handleZoomOut} sx={{ color: "white" }}>
            <ZoomOutIcon />
          </IconButton>
          <IconButton onClick={handleZoomIn} sx={{ color: "white" }}>
            <ZoomInIcon />
          </IconButton>
          <IconButton onClick={handleRotate} sx={{ color: "white" }}>
            <RotateRightIcon />
          </IconButton>
          <IconButton onClick={onClose} sx={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 400,
          p: 3,
        }}
      >
        <img
          src={imageUrl}
          alt={title}
          style={{
            maxWidth: "100%",
            maxHeight: "70vh",
            objectFit: "contain",
            transform: `scale(${zoom}) rotate(${rotation}deg)`,
            transition: "transform 0.3s ease",
          }}
        />
      </DialogContent>
    </Dialog>
  );
}

export default function TenantProfileEdit() {
  title("Edit Profile");
  const dispatch = useDispatch();
  const open = useSelector((state) => state.sidebar.open);
  const { theme: appTheme } = useAppTheme();

  // Feed the active OTS theme into MUI so every `color="primary"` control on
  // this page — buttons, chips, switches, sliders, focus rings — follows it.
  const muiTheme = React.useMemo(
    () =>
      createTheme({
        palette: {
          primary: {
            main: appTheme.primary,
            dark: appTheme.primaryHover,
            light: appTheme.lightBg,
            contrastText: "#fff",
          },
          secondary: { main: appTheme.secondary, contrastText: "#fff" },
        },
        shape: { borderRadius: 12 },
        components: {
          MuiButton: {
            styleOverrides: {
              root: { textTransform: "none", fontWeight: 600, borderRadius: 999 },
            },
          },
          MuiChip: { styleOverrides: { root: { fontWeight: 600 } } },
        },
      }),
    [appTheme]
  );

  const gradient = `linear-gradient(120deg, ${appTheme.primary} 0%, ${appTheme.secondary} 100%)`;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [hasAgreement, setHasAgreement] = useState(false);
  const [profileImage, setProfileImage] = useState(null);
  const fileInputRef = React.useRef(null);

  // Image Modal State
  const [modalOpen, setModalOpen] = useState(false);
  const [modalImage, setModalImage] = useState({ url: "", title: "" });

  // Pets and Vehicles State
  const [pets, setPets] = useState([]);
  const [vehicles, setVehicles] = useState([]);

  const [formData, setFormData] = useState({
    // Locked Fields (Never Editable)
    name: "",
    fatherName: "",
    cnicNo: "",
    cnicFront: "",
    cnicBack: "",

    // Editable Fields
    profilePhoto: "",
    mobileNumber: "",
    gender: "",
    maritalStatus: "",
    currentAddress: "",
    permanentAddress: "",
    tenantType: "",
    totalFamilyMembers: 1,
    language: [],
    cast: "",
    employmentStatus: "",
    companyName: "",
    designation: "",
    instituteName: "",
    monthlyIncome: "",
    incomeProof: "",
    hasPets: false,
    hasVehicle: false,
    preferredMoveIn: "",
    budgetMin: 10000,
    budgetMax: 50000,
    emergencyContactName: "",
    emergencyContactRelation: "",
    emergencyContactPhone: "",
    aboutMe: "",
    hasPreviousRental: false,
    previousLandlordName: "",
    previousLandlordPhone: "",
  });

  const [originalData, setOriginalData] = useState({});

  // Saving can break the rules of a property the tenant already requested or
  // booked. The server says so first (a dry run); we then ask before saving.
  const [cancelDialog, setCancelDialog] = useState({
    open: false,
    bookings: [],
    updateData: null,
  });

  useEffect(() => {
    fetchTenantProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTenantProfile = () => {
    setLoading(true);
    const userId =
      sessionStorage.getItem("user_id") || localStorage.getItem("user_id");
    dispatch(GetTenantProfile({ userId }))
      .then((res) => {
        if (res?.payload?.data) {
          const data = res.payload.data;
          const profileData = {
            ...data,
            mobileNumber: data.mobileNumber?.replace(/^03/, "") || "",
            emergencyContactPhone:
              data.emergencyContactPhone?.replace(/^03/, "") || "",
            previousLandlordPhone:
              data.previousLandlordPhone?.replace(/^03/, "") || "",
            language: data.language || [],
            budgetMin: data.budgetMin || 10000,
            budgetMax: data.budgetMax || 50000,
            hasPets: data.pets?.length > 0,
            hasVehicle: data.vehicles?.length > 0,
          };
          setFormData(profileData);
          setOriginalData(profileData);
          setProfileImage(data.profilePhoto);
          setHasAgreement(data.hasActiveAgreement || false);

          // Set pets and vehicles
          setPets(data.pets || []);
          setVehicles(data.vehicles || []);
        }
        setLoading(false);
      })
      .catch(() => {
        toast.error("Failed to load profile", { position: "top-center" });
        setLoading(false);
      });
  };
  console.log(originalData, "originalData");

  const handleChange = (field) => (e) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  // Family size: let the field go empty while the tenant types a new number,
  // then snap back to a valid whole number (1–30) when they leave the field.
  const handleFamilyMembersChange = (e) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, 2);
    setFormData((prev) => ({
      ...prev,
      totalFamilyMembers: digits === "" ? "" : Math.min(30, Number(digits)),
    }));
  };

  const handleFamilyMembersBlur = () => {
    setFormData((prev) => ({
      ...prev,
      totalFamilyMembers: Math.max(1, Number(prev.totalFamilyMembers) || 1),
    }));
  };

  const handlePhoneChange = (field) => (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 9);
    setFormData({ ...formData, [field]: value });
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image size should be less than 5MB", {
          position: "top-center",
        });
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileImage(reader.result);
        setFormData({ ...formData, profilePhoto: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageClick = (url, title) => {
    setModalImage({ url, title });
    setModalOpen(true);
  };

  // Pet Management Functions
  const handleAddPet = () => {
    setPets([...pets, { type: "cat", count: 1 }]);
  };

  const handleRemovePet = (index) => {
    setPets(pets.filter((_, i) => i !== index));
  };

  const handlePetChange = (index, field, value) => {
    const updatedPets = [...pets];
    updatedPets[index] = { ...updatedPets[index], [field]: value };
    setPets(updatedPets);
  };

  // Vehicle Management Functions
  const handleAddVehicle = () => {
    setVehicles([...vehicles, { type: "car", number: "" }]);
  };

  const handleRemoveVehicle = (index) => {
    setVehicles(vehicles.filter((_, i) => i !== index));
  };

  const handleVehicleChange = (index, field, value) => {
    const updatedVehicles = [...vehicles];
    console.log(updatedVehicles);
    if (value.length > 3 && !value.includes("-")) {
      value = value.slice(0, 3) + "-" + value.slice(3);
    }

    // Limit total length (ABC-1234 = 8 characters max)
    value = value.slice(0, 8);
    updatedVehicles[index] = { ...updatedVehicles[index], [field]: value };
    setVehicles(updatedVehicles);
  };

  const handleSave = () => {
    setSaving(true);
    const userId =
      sessionStorage.getItem("user_id") || localStorage.getItem("user_id");

    const updateData = {
      userId,
      profilePhoto: formData.profilePhoto,
      mobileNumber: formData.mobileNumber ? `03${formData.mobileNumber}` : "",
      currentAddress: formData.currentAddress,
      permanentAddress: formData.permanentAddress,
      language: formData.language,
      employmentStatus: formData.employmentStatus,
      companyName: formData.companyName,
      designation: formData.designation,
      instituteName: formData.instituteName,
      monthlyIncome: formData.monthlyIncome,
      hasPets: pets.length > 0,
      pets: pets.map((pet) => ({
        ...pet,
        count: Math.max(1, parseInt(pet.count) || 1),
      })),
      hasVehicle: vehicles.length > 0,
      vehicles: vehicles,
      preferredMoveIn: formData.preferredMoveIn,
      budgetMin: formData.budgetMin,
      budgetMax: formData.budgetMax,
      emergencyContactName: formData.emergencyContactName,
      emergencyContactRelation: formData.emergencyContactRelation,
      emergencyContactPhone: formData.emergencyContactPhone
        ? `03${formData.emergencyContactPhone}`
        : "",
      aboutMe: formData.aboutMe,
      hasPreviousRental: formData.hasPreviousRental,
      previousLandlordName: formData.previousLandlordName,
      previousLandlordPhone: formData.previousLandlordPhone
        ? `03${formData.previousLandlordPhone}`
        : "",
    };

    // Tenant type / family size can change even with an active agreement —
    // the server's assessProfileChange() checks the impact on any open
    // requests/bookings (a flat you've already moved into is never touched
    // by this) and asks for confirmation before cancelling anything.
    updateData.tenantType = formData.tenantType;
    updateData.totalFamilyMembers = Math.max(
      1,
      Math.min(30, Number(formData.totalFamilyMembers) || 1)
    );

    // Cast / gender / marital status stay locked once there's an active
    // agreement — unrelated to flat matching, so include them only when free.
    if (!hasAgreement) {
      updateData.cast = formData.cast;
      updateData.maritalStatus = formData.maritalStatus;
      updateData.gender = formData.gender;
    }

    // Ask first: would this edit break the rules of a property the tenant
    // already requested or booked? If so they must confirm before anything
    // is saved or cancelled.
    dispatch(PreviewTenantProfileUpdate(updateData))
      .then((res) => {
        if (res?.meta?.requestStatus !== "fulfilled") {
          toast.error(res?.payload?.message || "Failed to update profile", {
            position: "top-center",
          });
          setSaving(false);
          return;
        }

        if (res.payload?.requiresConfirmation) {
          setCancelDialog({
            open: true,
            bookings: res.payload.affectedBookings || [],
            updateData,
          });
          setSaving(false);
          return;
        }

        submitProfileUpdate(updateData, []);
      })
      .catch(() => {
        toast.error("Failed to update profile", { position: "top-center" });
        setSaving(false);
      });
  };

  // Does the real save. `confirmedRequestIds` are the requests/bookings the
  // tenant has agreed to have cancelled — the server refuses to cancel any
  // that are not on that list.
  const submitProfileUpdate = (updateData, confirmedRequestIds) => {
    setSaving(true);
    dispatch(
      UpdateTenantProfile({
        ...updateData,
        confirmCancelRequestIds: confirmedRequestIds,
      })
    )
      .then((res) => {
        // Something changed between the check and the save, so there is now
        // an affected booking the tenant has not seen: show it, do not save.
        if (res?.payload?.code === "BOOKING_CANCELLATION_REQUIRED") {
          setCancelDialog({
            open: true,
            bookings: res.payload.affectedBookings || [],
            updateData,
          });
          setSaving(false);
          return;
        }

        if (
          res?.payload?.status === 200 ||
          res?.meta?.requestStatus === "fulfilled"
        ) {
          const cancelled = res?.payload?.cancelledBookings || [];
          if (cancelled.length > 0) {
            const lines = cancelled.map((c) =>
              c.paid && !c.isCash && c.refundAmount !== null
                ? `${c.flatName}: refund Rs ${Number(c.refundAmount).toLocaleString()}`
                : c.flatName
            );
            toast.success(
              `Profile updated. Cancelled: ${lines.join("; ")}`,
              { position: "top-center", autoClose: 8000 }
            );
          } else {
            toast.success("Profile updated successfully!", {
              position: "top-center",
            });
          }
          if ((res?.payload?.failedCancellations || []).length > 0) {
            toast.error(
              "Your profile was updated, but some requests could not be cancelled automatically. Please check My Requests.",
              { position: "top-center", autoClose: 8000 }
            );
          }
          setOriginalData(formData);
          setIsEditing(false);
        } else {
          toast.error(res?.payload?.message || "Failed to update profile", {
            position: "top-center",
          });
        }
        setSaving(false);
        dispatch(ValidateUser({}));
      })
      .catch(() => {
        toast.error("Failed to update profile", { position: "top-center" });
        setSaving(false);
      });
  };

  const handleConfirmCancelBookings = () => {
    const { bookings, updateData } = cancelDialog;
    setCancelDialog({ open: false, bookings: [], updateData: null });
    submitProfileUpdate(
      updateData,
      bookings.map((b) => b.requestId)
    );
  };

  const handleKeepProfile = () => {
    setCancelDialog({ open: false, bookings: [], updateData: null });
  };

  const handleCancel = () => {
    setFormData(originalData);
    setProfileImage(originalData.profilePhoto);
    setIsEditing(false);
    // Reset pets and vehicles to original
    fetchTenantProfile();
  };

  if (loading) {
    return (
      <MuiThemeProvider theme={muiTheme}>
        <Wrapper />
        <div className={`${open ? "sidebar-open" : "sidebar-closed"}`}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "60vh",
            }}
          >
            <CircularProgress size={60} />
          </Box>
        </div>
      </MuiThemeProvider>
    );
  }

  return (
    <MuiThemeProvider theme={muiTheme}>
      <Wrapper />
      <div className={`${open ? "sidebar-open" : "sidebar-closed"}`}>
        <Container maxWidth="lg" sx={{ py: 4 }}>
          {/* Themed header */}
          <Box
            sx={{
              position: "relative",
              overflow: "hidden",
              borderRadius: "24px",
              background: gradient,
              color: "#fff",
              p: { xs: 2.5, md: 3.5 },
              mb: 4,
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", sm: "center" },
              gap: 2,
            }}
          >
            <Box
              sx={{
                position: "absolute",
                width: 230,
                height: 230,
                borderRadius: "50%",
                bgcolor: "rgba(255,255,255,0.12)",
                top: -110,
                right: -50,
              }}
            />
            <Box sx={{ position: "relative", display: "flex", alignItems: "center", gap: 2 }}>
              <Avatar
                src={profileImage}
                sx={{
                  width: 62,
                  height: 62,
                  border: "3px solid rgba(255,255,255,0.55)",
                  bgcolor: "rgba(255,255,255,0.2)",
                }}
              >
                <PersonIcon />
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontWeight: 800,
                    lineHeight: 1.2,
                    fontSize: { xs: "1.5rem", md: "2rem" },
                  }}
                >
                  My Profile
                </Typography>
                <Typography sx={{ opacity: 0.9, fontSize: { xs: 13, md: 14.5 } }}>
                  {formData?.name || "Keep your details up to date"}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ position: "relative" }}>
              {/* Editing used to also be blocked by any pending/hold/accepted
                  request (is_pending_flat_request) — but the owner's view of
                  an existing request always shows what was true when it was
                  sent (a stored snapshot, not a live lookup), and the server
                  now re-checks + withdraws any open request a profile edit
                  breaks. Blocking the whole profile just because one request
                  was open had no protective purpose left and only trapped
                  tenants who needed to update their profile for a DIFFERENT
                  property. */}
              {!originalData?.is_tenant && (
                  <>
                    {!isEditing ? (
                      <Button
                        variant="contained"
                        startIcon={<EditIcon />}
                        onClick={() => setIsEditing(true)}
                        sx={{
                          bgcolor: "#fff",
                          color: appTheme.primary,
                          px: 2.5,
                          boxShadow: "0 10px 24px -12px rgba(0,0,0,0.5)",
                          "&:hover": { bgcolor: "rgba(255,255,255,0.9)" },
                        }}
                      >
                        Edit Profile
                      </Button>
                    ) : (
                      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                        <Button
                          variant="outlined"
                          startIcon={<CancelIcon />}
                          onClick={handleCancel}
                          disabled={saving}
                          sx={{
                            color: "#fff",
                            borderColor: "rgba(255,255,255,0.6)",
                            "&:hover": {
                              borderColor: "#fff",
                              bgcolor: "rgba(255,255,255,0.14)",
                            },
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="contained"
                          startIcon={
                            saving ? (
                              <CircularProgress size={20} color="inherit" />
                            ) : (
                              <SaveIcon />
                            )
                          }
                          onClick={handleSave}
                          disabled={saving}
                          sx={{
                            bgcolor: "#fff",
                            color: appTheme.primary,
                            px: 2.5,
                            boxShadow: "0 10px 24px -12px rgba(0,0,0,0.5)",
                            "&:hover": { bgcolor: "rgba(255,255,255,0.9)" },
                          }}
                        >
                          {saving ? "Saving..." : "Save Changes"}
                        </Button>
                      </Box>
                    )}
                  </>
                )}
            </Box>
          </Box>

          {hasAgreement && (
            <Alert severity="info" sx={{ mb: 3 }}>
              You have an active agreement. Some fields (Cast, Gender, Marital
              Status) are locked and cannot be changed. You can still update
              Tenant Type and Family Members — if that would affect an
              existing request or booking, we'll ask you to confirm first.
            </Alert>
          )}

          {/* Profile Photo & Basic Info (Locked) */}
          <StyledCard>
            <CardContent>
              <SectionHeader>
                <VerifiedUserIcon color="primary" />
                <Typography variant="h6" fontWeight={600}>
                  Identity Information
                </Typography>
                <Chip
                  label="Verified & Locked"
                  size="small"
                  color="warning"
                  icon={<LockIcon sx={{ fontSize: 16 }} />}
                />
                <Typography
                  variant="p"
                  fontWeight={400}
                  sx={{
                    marginLeft: "auto",
                    fontStyle: "italic",
                    color: "#00000066",
                  }}
                >
                  Email: {formData?.email}
                </Typography>
              </SectionHeader>

              <Grid container spacing={3}>
                {/* Profile Photo */}
                <Grid
                  item
                  xs={12}
                  sx={{ display: "flex", justifyContent: "center", mb: 2 }}
                >
                  <Box sx={{ textAlign: "center" }}>
                    <PhotoUploadBox
                      onClick={() => isEditing && fileInputRef.current?.click()}
                    >
                      {profileImage ? (
                        <Avatar
                          src={profileImage}
                          sx={{ width: "100%", height: "100%" }}
                        />
                      ) : (
                        <Box sx={{ textAlign: "center", color: "#999" }}>
                          <CameraAltIcon sx={{ fontSize: 32 }} />
                          <Typography variant="caption" display="block">
                            Add Photo
                          </Typography>
                        </Box>
                      )}
                    </PhotoUploadBox>
                    <input
                      type="file"
                      ref={fileInputRef}
                      hidden
                      accept="image/*"
                      onChange={handleImageUpload}
                      disabled={!isEditing}
                    />
                    {isEditing && (
                      <Typography
                        variant="caption"
                        color="primary"
                        sx={{ mt: 1, display: "block", cursor: "pointer" }}
                      >
                        Click to change photo
                      </Typography>
                    )}
                  </Box>
                </Grid>

                {/* Locked Fields */}
                <Grid item xs={12} sm={6}>
                  <LockedField>
                    <LockBadge>
                      <LockIcon sx={{ fontSize: 14, color: "#fff" }} />
                    </LockBadge>
                    <TextField
                      fullWidth
                      label="Full Name"
                      value={formData.name || ""}
                      disabled
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </LockedField>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <LockedField>
                    <LockBadge>
                      <LockIcon sx={{ fontSize: 14, color: "#fff" }} />
                    </LockBadge>
                    <TextField
                      fullWidth
                      label="Father's Name"
                      value={formData.fatherName || ""}
                      disabled
                    />
                  </LockedField>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <LockedField>
                    <LockBadge>
                      <LockIcon sx={{ fontSize: 14, color: "#fff" }} />
                    </LockBadge>
                    <TextField
                      fullWidth
                      label="CNIC Number"
                      value={formData.cnicNo || ""}
                      disabled
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <BadgeIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </LockedField>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Mobile Number"
                    value={formData.mobileNumber || ""}
                    onChange={handlePhoneChange("mobileNumber")}
                    disabled={!isEditing}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">03</InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                {/* Gender & Marital Status - Locked after agreement */}
                <Grid item xs={12} sm={6}>
                  {hasAgreement ? (
                    <LockedField>
                      <LockBadge>
                        <LockIcon sx={{ fontSize: 14, color: "#fff" }} />
                      </LockBadge>
                      <TextField
                        fullWidth
                        label="Gender"
                        value={
                          formData.gender === "male"
                            ? "Male"
                            : formData.gender === "female"
                            ? "Female"
                            : "Other"
                        }
                        disabled
                      />
                    </LockedField>
                  ) : (
                    <FormControl fullWidth disabled={!isEditing}>
                      <InputLabel>Gender</InputLabel>
                      <Select
                        value={formData.gender || ""}
                        label="Gender"
                        onChange={handleChange("gender")}
                      >
                        <MenuItem value="male">Male</MenuItem>
                        <MenuItem value="female">Female</MenuItem>
                        <MenuItem value="other">Other</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                </Grid>

                <Grid item xs={12} sm={6}>
                  {hasAgreement ? (
                    <LockedField>
                      <LockBadge>
                        <LockIcon sx={{ fontSize: 14, color: "#fff" }} />
                      </LockBadge>
                      <TextField
                        fullWidth
                        label="Marital Status"
                        value={
                          formData.maritalStatus === "married"
                            ? "Married"
                            : "Single"
                        }
                        disabled
                      />
                    </LockedField>
                  ) : (
                    <FormControl fullWidth disabled={!isEditing}>
                      <InputLabel>Marital Status</InputLabel>
                      <Select
                        value={formData.maritalStatus || ""}
                        label="Marital Status"
                        onChange={handleChange("maritalStatus")}
                      >
                        <MenuItem value="unmarried">Single</MenuItem>
                        <MenuItem value="married">Married</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </StyledCard>

          {/* CNIC & Income Documents */}
          <StyledCard>
            <CardContent>
              <SectionHeader>
                <DescriptionIcon color="primary" />
                <Typography variant="h6" fontWeight={600}>
                  Documents
                </Typography>
                <Chip
                  label="Verified & Locked"
                  size="small"
                  color="warning"
                  icon={<LockIcon sx={{ fontSize: 16 }} />}
                />
              </SectionHeader>

              <Grid container spacing={3}>
                {/* CNIC Front */}
                <Grid item xs={12} sm={4}>
                  <DocumentCard
                    onClick={() =>
                      handleImageClick(formData.cnicFront, "CNIC Front")
                    }
                  >
                    <CardContent sx={{ textAlign: "center", p: 2 }}>
                      <BadgeIcon
                        sx={{ fontSize: 40, color: "primary.main", mb: 1 }}
                      />
                      <Typography variant="subtitle1" fontWeight={600} mb={1}>
                        CNIC Front
                      </Typography>
                      {formData.cnicFront ? (
                        <CardMedia
                          component="img"
                          height="150"
                          image={formData.cnicFront}
                          alt="CNIC Front"
                          sx={{
                            borderRadius: 1,
                            objectFit: "cover",
                            border: "2px solid #e0e0e0",
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            height: 150,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: "#f5f5f5",
                            borderRadius: 1,
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            No document
                          </Typography>
                        </Box>
                      )}
                      <Typography
                        variant="caption"
                        color="primary"
                        display="block"
                        mt={1}
                      >
                        Click to view
                      </Typography>
                    </CardContent>
                  </DocumentCard>
                </Grid>

                {/* CNIC Back */}
                <Grid item xs={12} sm={4}>
                  <DocumentCard
                    onClick={() =>
                      handleImageClick(formData.cnicBack, "CNIC Back")
                    }
                  >
                    <CardContent sx={{ textAlign: "center", p: 2 }}>
                      <BadgeIcon
                        sx={{ fontSize: 40, color: "primary.main", mb: 1 }}
                      />
                      <Typography variant="subtitle1" fontWeight={600} mb={1}>
                        CNIC Back
                      </Typography>
                      {formData.cnicBack ? (
                        <CardMedia
                          component="img"
                          height="150"
                          image={formData.cnicBack}
                          alt="CNIC Back"
                          sx={{
                            borderRadius: 1,
                            objectFit: "cover",
                            border: "2px solid #e0e0e0",
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            height: 150,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: "#f5f5f5",
                            borderRadius: 1,
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            No document
                          </Typography>
                        </Box>
                      )}
                      <Typography
                        variant="caption"
                        color="primary"
                        display="block"
                        mt={1}
                      >
                        Click to view
                      </Typography>
                    </CardContent>
                  </DocumentCard>
                </Grid>

                {/* Income Proof */}
                {formData?.employmentStatus === "student" ? (
                  ""
                ) : (
                  <Grid item xs={12} sm={4}>
                    <DocumentCard
                      onClick={() =>
                        handleImageClick(formData.incomeProof, "Income Proof")
                      }
                    >
                      <CardContent sx={{ textAlign: "center", p: 2 }}>
                        <AccountBalanceWalletIcon
                          sx={{ fontSize: 40, color: "#4caf50", mb: 1 }}
                        />
                        <Typography variant="subtitle1" fontWeight={600} mb={1}>
                          Income Proof
                        </Typography>
                        {formData.incomeProof ? (
                          <CardMedia
                            component="img"
                            height="150"
                            image={formData.incomeProof}
                            alt="Income Proof"
                            sx={{
                              borderRadius: 1,
                              objectFit: "cover",
                              border: "2px solid #e0e0e0",
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              height: 150,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              bgcolor: "#f5f5f5",
                              borderRadius: 1,
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              No document
                            </Typography>
                          </Box>
                        )}
                        <Typography
                          variant="caption"
                          color="primary"
                          display="block"
                          mt={1}
                        >
                          Click to view
                        </Typography>
                      </CardContent>
                    </DocumentCard>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </StyledCard>

          {/* Address & Family */}
          <StyledCard>
            <CardContent>
              <SectionHeader>
                <HomeIcon color="primary" />
                <Typography variant="h6" fontWeight={600}>
                  Address & Family
                </Typography>
              </SectionHeader>

              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Current Address"
                    multiline
                    rows={2}
                    value={formData.currentAddress || ""}
                    onChange={handleChange("currentAddress")}
                    disabled={!isEditing}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Permanent Address"
                    multiline
                    rows={2}
                    value={formData.permanentAddress || ""}
                    onChange={handleChange("permanentAddress")}
                    disabled={!isEditing}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth disabled={!isEditing}>
                    <InputLabel>Tenant Type</InputLabel>
                    <Select
                      value={formData.tenantType || ""}
                      label="Tenant Type"
                      onChange={handleChange("tenantType")}
                    >
                      <MenuItem value="family">Family</MenuItem>
                      <MenuItem value="bachelors">Bachelors</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Total Family Members"
                    type="number"
                    // Keep "" while typing — falling back to 1 here made the
                    // field impossible to clear, so 1 could never become 5.
                    value={formData.totalFamilyMembers ?? ""}
                    onChange={handleFamilyMembersChange}
                    onBlur={handleFamilyMembersBlur}
                    disabled={!isEditing}
                    inputProps={{ min: 1, max: 30, step: 1 }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth disabled={!isEditing}>
                    <InputLabel>Languages Spoken</InputLabel>
                    <Select
                      multiple
                      value={formData.language || []}
                      onChange={handleChange("language")}
                      input={<OutlinedInput label="Languages Spoken" />}
                      renderValue={(selected) => (
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {selected.map((lang) => (
                            <Chip key={lang} label={lang} size="small" />
                          ))}
                        </Box>
                      )}
                    >
                      {pakistaniLanguages.map((lang) => (
                        <MenuItem key={lang} value={lang}>
                          {lang}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  {hasAgreement ? (
                    <LockedField>
                      <LockBadge>
                        <LockIcon sx={{ fontSize: 14, color: "#fff" }} />
                      </LockBadge>
                      <TextField
                        fullWidth
                        label="Cast / Community"
                        value={formData.cast || ""}
                        disabled
                      />
                    </LockedField>
                  ) : (
                    <TextField
                      fullWidth
                      label="Cast / Community"
                      value={formData.cast || ""}
                      onChange={handleChange("cast")}
                      disabled={!isEditing}
                    />
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </StyledCard>

          {/* Employment */}
          <StyledCard>
            <CardContent>
              <SectionHeader>
                <WorkIcon color="primary" />
                <Typography variant="h6" fontWeight={600}>
                  Employment & Financial
                </Typography>
              </SectionHeader>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth disabled={!isEditing}>
                    <InputLabel>Employment Status</InputLabel>
                    <Select
                      value={formData.employmentStatus || ""}
                      label="Employment Status"
                      onChange={handleChange("employmentStatus")}
                    >
                      <MenuItem value="employed">Employed</MenuItem>
                      <MenuItem value="self_employed">Self Employed</MenuItem>
                      <MenuItem value="student">Student</MenuItem>
                      <MenuItem value="retired">Retired</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth disabled={!isEditing}>
                    <InputLabel>Monthly Income</InputLabel>
                    <Select
                      value={formData.monthlyIncome || ""}
                      label="Monthly Income"
                      onChange={handleChange("monthlyIncome")}
                    >
                      {incomeRanges.map((range) => (
                        <MenuItem key={range.value} value={range.value}>
                          Rs. {range.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {(formData.employmentStatus === "employed" ||
                  formData.employmentStatus === "self_employed") && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label={
                          formData.employmentStatus === "self_employed"
                            ? "Business Name"
                            : "Company Name"
                        }
                        value={formData.companyName || ""}
                        onChange={handleChange("companyName")}
                        disabled={!isEditing}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <BusinessIcon color="action" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    {formData.employmentStatus === "employed" && (
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Designation"
                          value={formData.designation || ""}
                          onChange={handleChange("designation")}
                          disabled={!isEditing}
                        />
                      </Grid>
                    )}
                  </>
                )}

                {formData.employmentStatus === "student" && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Institution Name"
                      value={formData.instituteName || ""}
                      onChange={handleChange("instituteName")}
                      disabled={!isEditing}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SchoolIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </StyledCard>

          {/* Lifestyle - Pets & Vehicles */}
          <StyledCard>
            <CardContent>
              <SectionHeader>
                <PetsIcon color="primary" />
                <Typography variant="h6" fontWeight={600}>
                  Lifestyle & Preferences
                </Typography>
              </SectionHeader>

              <Grid container spacing={3}>
                {/* Pets Section */}
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 2,
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight={600}>
                      <PetsIcon sx={{ verticalAlign: "middle", mr: 1 }} />
                      Pets
                    </Typography>
                    {isEditing && (
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={handleAddPet}
                        variant="outlined"
                      >
                        Add Pet
                      </Button>
                    )}
                  </Box>

                  {pets.length > 0 ? (
                    <List>
                      {pets.map((pet, index) => (
                        <ListItem
                          key={index}
                          sx={{
                            bgcolor: "#f5f5f5",
                            borderRadius: 2,
                            mb: 1,
                            display: "flex",
                            gap: 2,
                          }}
                        >
                          <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={5}>
                              <FormControl
                                fullWidth
                                size="small"
                                disabled={!isEditing}
                              >
                                <InputLabel>Pet Type</InputLabel>
                                <Select
                                  value={pet.type || ""}
                                  label="Pet Type"
                                  onChange={(e) =>
                                    handlePetChange(
                                      index,
                                      "type",
                                      e.target.value
                                    )
                                  }
                                >
                                  {petTypes.map((type) => (
                                    <MenuItem
                                      key={type.value}
                                      value={type.value}
                                    >
                                      {type.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={5}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Count"
                                type="number"
                                value={pet.count ?? ""}
                                onChange={(e) =>
                                  handlePetChange(
                                    index,
                                    "count",
                                    e.target.value === ""
                                      ? ""
                                      : Math.max(1, parseInt(e.target.value) || 1)
                                  )
                                }
                                onBlur={() =>
                                  handlePetChange(
                                    index,
                                    "count",
                                    Math.max(1, parseInt(pet.count) || 1)
                                  )
                                }
                                disabled={!isEditing}
                                inputProps={{ min: 1 }}
                              />
                            </Grid>
                            {isEditing && (
                              <Grid item xs={12} sm={2}>
                                <IconButton
                                  color="error"
                                  onClick={() => handleRemovePet(index)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Grid>
                            )}
                          </Grid>
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ py: 2 }}
                    >
                      No pets added
                    </Typography>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <Divider />
                </Grid>

                {/* Vehicles Section */}
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 2,
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight={600}>
                      <DirectionsCarIcon
                        sx={{ verticalAlign: "middle", mr: 1 }}
                      />
                      Vehicles
                    </Typography>
                    {isEditing && (
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={handleAddVehicle}
                        variant="outlined"
                      >
                        Add Vehicle
                      </Button>
                    )}
                  </Box>

                  {vehicles.length > 0 ? (
                    <List>
                      {vehicles.map((vehicle, index) => (
                        <ListItem
                          key={index}
                          sx={{
                            bgcolor: "#f5f5f5",
                            borderRadius: 2,
                            mb: 1,
                            display: "flex",
                            gap: 2,
                          }}
                        >
                          <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={5}>
                              <FormControl
                                fullWidth
                                size="small"
                                disabled={!isEditing}
                              >
                                <InputLabel>Vehicle Type</InputLabel>
                                <Select
                                  value={vehicle.type || ""}
                                  label="Vehicle Type"
                                  onChange={(e) =>
                                    handleVehicleChange(
                                      index,
                                      "type",
                                      e.target.value
                                    )
                                  }
                                >
                                  {vehicleTypes.map((type) => (
                                    <MenuItem
                                      key={type.value}
                                      value={type.value}
                                    >
                                      {type.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={5}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Vehicle Number"
                                value={vehicle.number || ""}
                                onChange={(e) =>
                                  handleVehicleChange(
                                    index,
                                    "number",
                                    e.target.value.toUpperCase()
                                  )
                                }
                                disabled={!isEditing}
                                placeholder="ABC-123"
                              />
                            </Grid>
                            {isEditing && (
                              <Grid item xs={12} sm={2}>
                                <IconButton
                                  color="error"
                                  onClick={() => handleRemoveVehicle(index)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Grid>
                            )}
                          </Grid>
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ py: 2 }}
                    >
                      No vehicles added
                    </Typography>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <Divider />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth disabled={!isEditing}>
                    <InputLabel>Preferred Move-in</InputLabel>
                    <Select
                      value={formData.preferredMoveIn || ""}
                      label="Preferred Move-in"
                      onChange={handleChange("preferredMoveIn")}
                    >
                      {moveInOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    <AccountBalanceWalletIcon
                      sx={{ verticalAlign: "middle", mr: 1 }}
                    />
                    Budget Range: Rs. {formData.budgetMin?.toLocaleString()} -
                    Rs. {formData.budgetMax?.toLocaleString()}
                  </Typography>
                  <Slider
                    value={[
                      formData.budgetMin || 10000,
                      formData.budgetMax || 50000,
                    ]}
                    onChange={(e, val) =>
                      setFormData({
                        ...formData,
                        budgetMin: val[0],
                        budgetMax: val[1],
                      })
                    }
                    valueLabelDisplay="auto"
                    valueLabelFormat={(val) => `Rs. ${val.toLocaleString()}`}
                    min={5000}
                    max={200000}
                    step={5000}
                    disabled={!isEditing}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </StyledCard>

          {/* Emergency Contact */}
          <StyledCard>
            <CardContent>
              <SectionHeader>
                <ContactPhoneIcon color="primary" />
                <Typography variant="h6" fontWeight={600}>
                  Emergency Contact
                </Typography>
              </SectionHeader>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Contact Person Name"
                    value={formData.emergencyContactName || ""}
                    onChange={handleChange("emergencyContactName")}
                    disabled={!isEditing}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth disabled={!isEditing}>
                    <InputLabel>Relationship</InputLabel>
                    <Select
                      value={formData.emergencyContactRelation || ""}
                      label="Relationship"
                      onChange={handleChange("emergencyContactRelation")}
                    >
                      <MenuItem value="father">Father</MenuItem>
                      <MenuItem value="mother">Mother</MenuItem>
                      <MenuItem value="spouse">Spouse</MenuItem>
                      <MenuItem value="sibling">Sibling</MenuItem>
                      <MenuItem value="friend">Friend</MenuItem>
                      <MenuItem value="relative">Other Relative</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Contact Number"
                    value={formData.emergencyContactPhone || ""}
                    onChange={handlePhoneChange("emergencyContactPhone")}
                    disabled={!isEditing}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">03</InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="About Me"
                    multiline
                    rows={3}
                    value={formData.aboutMe || ""}
                    onChange={handleChange("aboutMe")}
                    disabled={!isEditing}
                    helperText={`${
                      (formData.aboutMe || "").length
                    }/300 characters`}
                    inputProps={{ maxLength: 300 }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.hasPreviousRental || false}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            hasPreviousRental: e.target.checked,
                          })
                        }
                        disabled={!isEditing}
                      />
                    }
                    label="I have previous rental experience"
                  />
                  <Collapse in={formData.hasPreviousRental}>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Previous Landlord Name"
                          value={formData.previousLandlordName || ""}
                          onChange={handleChange("previousLandlordName")}
                          disabled={!isEditing}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Landlord Phone"
                          value={formData.previousLandlordPhone || ""}
                          onChange={handlePhoneChange("previousLandlordPhone")}
                          disabled={!isEditing}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                03
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Collapse>
                </Grid>
              </Grid>
            </CardContent>
          </StyledCard>

          {/* Digital Signature Section */}
          <StyledCard>
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <SectionHeader>
                <DrawIcon color="primary" />
                <Typography variant="h6" fontWeight={600}>
                  Digital Signature
                </Typography>
              </SectionHeader>
              <Box sx={{ textAlign: "center", py: 2 }}>
                {formData?.signature ? (
                  <>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        maxWidth: 300,
                        mx: "auto",
                        bgcolor: "#f5fff5",
                        borderColor: "#4caf50",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 1,
                          mb: 1,
                        }}
                      >
                        <CheckCircleIcon
                          sx={{ color: "#4caf50", fontSize: 20 }}
                        />
                        <Typography variant="subtitle2" color="success.main">
                          Signature Captured
                        </Typography>
                      </Box>
                      <Box
                        component="img"
                        src={formData.signature}
                        alt="Your Signature"
                        sx={{
                          maxWidth: "100%",
                          maxHeight: 100,
                          border: "1px solid #e0e0e0",
                          borderRadius: 1,
                          bgcolor: "white",
                          cursor: "pointer",
                        }}
                        onClick={() =>
                          handleImageClick(
                            formData.signature,
                            "Digital Signature"
                          )
                        }
                      />
                    </Paper>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 2, display: "block" }}
                    >
                      This signature was captured during registration and is
                      used for all your agreements.
                    </Typography>
                  </>
                ) : (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 3,
                      maxWidth: 300,
                      mx: "auto",
                      bgcolor: "#fff3e0",
                      borderColor: "#ff9800",
                    }}
                  >
                    <DrawIcon
                      sx={{
                        fontSize: 40,
                        color: "#ff9800",
                        opacity: 0.7,
                        mb: 1,
                      }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      No signature on file
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Please complete your registration to add your digital
                      signature.
                    </Typography>
                  </Paper>
                )}
              </Box>
            </CardContent>
          </StyledCard>

          <Box sx={{ mb: 3 }}>
            <InstallAppCard />
          </Box>
        </Container>
      </div>

      {/* Image Modal */}
      <ImageModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        imageUrl={modalImage.url}
        title={modalImage.title}
      />

      <BookingCancellationDialog
        open={cancelDialog.open}
        bookings={cancelDialog.bookings}
        submitting={saving}
        onKeep={handleKeepProfile}
        onConfirm={handleConfirmCancelBookings}
      />

      <ToastContainer />
    </MuiThemeProvider>
  );
}
